import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LinkIcon from '@mui/icons-material/Link'

import Button from '@/v2/components/common/Button'
import Typography from '@/v2/components/common/Typography'

import { usePatientContext } from '@/v2/contexts/PatientContext'
import {
  useFamilyMembers,
  useFamilyMemberLinks,
  RELATIONSHIP_OPTIONS,
} from '@/v2/hooks/patient/useFamilyMembers'
import FamilyMembersModal from '../FamilyMembersModal'

const Section = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eee;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const HeaderToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`

const MemberItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-of-type {
    border-bottom: none;
  }
`

const LinkedMemberItem = styled(MemberItem).attrs({
  as: 'button',
  type: 'button',
})`
  background: none;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  border-radius: 0;
  width: 100%;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;

  &:last-of-type {
    border-bottom: none;
  }

  &:active {
    background-color: #f7f7f7;
  }
`

const LinkItemButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  background: none;
  border: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
  width: 100%;
`

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const NameRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const PrimaryPill = styled.span`
  background-color: #e8e8e8;
  color: #4a4a4a;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  flex-shrink: 0;
`

const LinksBlock = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 12px 0;
  color: #999;
`

const RELATIONSHIP_LABELS = RELATIONSHIP_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label
  return acc
}, {})

const formatRelationship = value =>
  RELATIONSHIP_LABELS[value] || value || 'Family member'

const FamilyMembersMobile = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { patient } = usePatientContext()
  const patientId = patient?.id

  const { data: members } = useFamilyMembers(patientId)
  const { data: inverseLinks } = useFamilyMemberLinks(patientId)

  const [expanded, setExpanded] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const goToPatient = id => navigate(`/v2/patient/${id}/profile`)

  const count = members.length

  return (
    <Section>
      <HeaderRow>
        <HeaderToggle onClick={() => setExpanded(prev => !prev)}>
          <Typography variant="h4" fontSize="14px" fontWeight="600">
            Family Members ({count})
          </Typography>
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </HeaderToggle>
        <Button
          variant="text"
          onClick={() => setShowModal(true)}
          sx={{
            color: theme.palette.gray?.dark || '#666',
            padding: 0,
            minWidth: 'auto',
          }}
        >
          <Typography variant="h5" fontSize="12px">
            Edit
          </Typography>
        </Button>
      </HeaderRow>

      {expanded && (
        <>
          {count === 0 ? (
            <EmptyState>
              <Typography variant="h5" fontSize="14px">
                No family members yet
              </Typography>
            </EmptyState>
          ) : (
            members.map(member => {
              const ItemWrapper = member.relatedPatientId
                ? LinkedMemberItem
                : MemberItem
              const wrapperProps = member.relatedPatientId
                ? { onClick: () => goToPatient(member.relatedPatientId) }
                : {}
              return (
                <ItemWrapper key={member.id} {...wrapperProps}>
                  <MemberInfo>
                    <NameRow>
                      <Typography bold fontSize="14px">
                        {member.fullName}
                      </Typography>
                      {member.relatedPatientId && (
                        <LinkIcon
                          fontSize="small"
                          sx={{ color: theme.palette.gray?.dark || '#666' }}
                        />
                      )}
                    </NameRow>
                    <Typography
                      fontSize="12px"
                      sx={{ color: theme.palette.gray?.dark || '#666' }}
                    >
                      {formatRelationship(member.relationship)}
                    </Typography>
                  </MemberInfo>
                  {member.main && <PrimaryPill>PRIMARY</PrimaryPill>}
                </ItemWrapper>
              )
            })
          )}

          {inverseLinks.length > 0 && (
            <LinksBlock>
              <Typography
                variant="h5"
                fontSize="12px"
                sx={{
                  color: theme.palette.gray?.dark || '#666',
                  marginBottom: '6px',
                }}
              >
                Listed as family member of
              </Typography>
              {inverseLinks.map(link => (
                <LinkItemButton
                  key={link.id}
                  type="button"
                  onClick={() => goToPatient(link.patientId)}
                >
                  <LinkIcon
                    fontSize="small"
                    sx={{ color: theme.palette.gray?.dark || '#666' }}
                  />
                  <Typography fontSize="13px">
                    {formatRelationship(link.relationship)} of{' '}
                    {link.patientName}
                  </Typography>
                </LinkItemButton>
              ))}
            </LinksBlock>
          )}
        </>
      )}

      {showModal && (
        <FamilyMembersModal
          isOpen
          onClose={() => setShowModal(false)}
          patientId={patientId}
          fullScreen
        />
      )}
    </Section>
  )
}

export default FamilyMembersMobile
